import { Input } from '@mui/joy';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { type Nullable } from '~/common/types';

import CaseRole from '~/common/enums/caseRole';

import useDuplicateReferenceCases from '@/shared/hooks/useDuplicateReferenceCases';
import useEvent from '@/shared/hooks/useEvent';

import DuplicateReferenceWarning, {
  DuplicateReferenceWarningDisplay,
} from '@/shared/components/2.0/DuplicateReferenceWarning';
import { type DuplicateCase } from '@/shared/utils/duplicateCase';

type InvolvedPartyReferenceCellProps = {
  onChange: (reference: string) => void;
  onDuplicateCasesChange: (cases: DuplicateCase[]) => void;
  reference: Nullable<string>;
  role?: Nullable<CaseRole>;
};

/**
 * Reference cell of the involved parties table, warning when an insurer reference is already in use
 * on another case.
 *
 * Lives in its own component because the duplicate lookup is a hook and therefore cannot be called
 * per row from inside the table's cell renderer.
 */
const InvolvedPartyReferenceCell: React.FC<InvolvedPartyReferenceCellProps> = ({
  onChange,
  onDuplicateCasesChange,
  reference,
  role,
}) => {
  const { t } = useTranslation();

  const isInsurer = role === CaseRole.Insurer;
  const hasReference = !!reference?.trim();

  const { cases, isFetching } = useDuplicateReferenceCases({
    enabled: isInsurer && hasReference,
    reference,
    role: CaseRole.Insurer,
  });

  const handleDuplicateCasesChange = useEvent(onDuplicateCasesChange);

  useEffect(() => {
    handleDuplicateCasesChange(isFetching ? [] : cases);
  }, [cases, handleDuplicateCasesChange, isFetching]);

  return (
    <Input
      placeholder={t('typeHere')}
      startDecorator={
        isInsurer && hasReference ? (
          <DuplicateReferenceWarning
            showSuccess
            cases={cases}
            display={DuplicateReferenceWarningDisplay.Decorator}
            isFetching={isFetching}
          />
        ) : undefined
      }
      value={reference ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

export default InvolvedPartyReferenceCell;
